import { cn } from '@/shadcn-ui/utils';
import CheckBadge from '@/icons/check-badge';
import SecuredByStripeBlurb from './secured-by-stripe-blurb';
import { PaymentElement } from '@stripe/react-stripe-js';
import { PaymentTitle, PaymentType } from '@/account/types/payment-method-configuration';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { useState } from 'react';
import StripeCardholderName from '@/checkout/components/stripe-cardholder-name';
import StatusBanner from '@/common/components/status-banner';
import Spacer from '@/common/components/spacer';

export type StripePaymentProps = {
  showSecurityBlurb?: boolean;
  showCancellationBlurb?: boolean;
  showMobileACHWarning?: boolean;
  showPaymentTitle?: boolean;
  onPaymentChange?: (event: StripePaymentElementChangeEvent) => void;
  paymentError?: string;
  paymentType?: PaymentType;
  className?: string;
};

export default function StripePayment({
  showSecurityBlurb = true,
  showCancellationBlurb = false,
  showMobileACHWarning = false,
  showPaymentTitle = true,
  onPaymentChange,
  paymentError,
  paymentType = PaymentType.card,
  className,
}: StripePaymentProps) {
  const [ready, setReady] = useState(false);
  const [usingStoredPayment, setUsingStoredPayment] = useState(false);

  const handlePaymentChange = (event: StripePaymentElementChangeEvent) => {
    setUsingStoredPayment(
      event.complete && event.value.payment_method?.id ? true : false
    );
    onPaymentChange?.(event);
  };

  return (
    <>
      <div
        className={cn(
          'fs-exclude flex flex-col gap-2 rounded-t-2xl bg-flexcar-neutral85 p-4',
          className
        )}
      >
        {showMobileACHWarning ? (
          <>
            <StatusBanner
              variant="warning"
              title="Please continue from a web browser"
              message={{
                text: 'Linking your bank account to your Flexcar account will require separate windows to be opened to sign in to your bank which cannot be done through the Flexcar mobile app.',
              }}
            />
            <Spacer variant="height16" />
          </>
        ) : null}

        <div className={cn({ 'pointer-events-none opacity-30': showMobileACHWarning })}>
          {showPaymentTitle ? (
            <>
              <p className="body-strong">
                {paymentType === PaymentType.card ? PaymentTitle.card : PaymentTitle.ach}
              </p>
              <Spacer variant="height8" />
            </>
          ) : null}

          <StripeCardholderName
            stripePaymentElementRendered={ready}
            usingStoredPayment={usingStoredPayment}
            paymentError={paymentError}
            paymentType={paymentType}
          />

          <PaymentElement
            options={{
              fields: {
                billingDetails: {
                  name: 'never',
                  email: 'never',
                  phone: 'never',
                  address: 'never',
                },
              },
            }}
            onChange={handlePaymentChange}
            onReady={() => setReady(true)}
          />
        </div>
      </div>

      {showSecurityBlurb ? (
        <SecuredByStripeBlurb
          className={cn('caption-regular bg-flexcar-neutral85 px-4 pb-4 font-medium', {
            'rounded-b-[12px]': !showCancellationBlurb,
          })}
        />
      ) : null}

      {showCancellationBlurb ? (
        <div className="flex items-center justify-center gap-[8px] rounded-b-[12px] bg-flexcar-neutral80 px-[16px] py-[16px]">
          <CheckBadge className="flex-shrink-0" />
          <p className="caption-regular font-semibold tracking-[0.12px] text-flexcar-neutral0">
            Free cancellation within 24 hours of booking
          </p>
        </div>
      ) : null}

      {!showSecurityBlurb && !showCancellationBlurb ? (
        <div className="rounded-b-2xl bg-flexcar-neutral85 pt-[16px]" />
      ) : null}
    </>
  );
}
